import {
  documentToReactComponents,
  Options as ContentfulOptions,
} from '@contentful/rich-text-react-renderer';
import { MARKS, BLOCKS, INLINES } from '@contentful/rich-text-types';
import { get } from 'lodash';
import React from 'react';

import { TextField } from 'Containers/App/types';
import { TooltipUnderline } from 'Containers/ResultPage/Recommendation/styled';
import { RichTextContent as RichTextContentType } from 'ContentfulDefaults/types/_shared';
import { usePremiumContext } from 'Contexts/premiumContext';
import { useTextContext } from 'Contexts/textContext';
import { FormattedTooltipProps } from 'DesignLibrary/atoms';
import { Tooltip } from 'DesignLibrary/atoms/Tooltip';
import Logging from 'Utils/logger';
import { applyTextReplacements, PREMIUM_TEXT_REPLACEMENT, TextReplacements } from 'Utils/textReplacement';

import { replaceVarsInNode } from './replaceVarsInNode';
import { RichTextStyleWrapper, type RichTextStyleWrapperProps } from './styled';
import { type ReplacementVars } from './types';

export interface RichTextProps {
  field: TextField | undefined; // Contentful key, eg. 'coverageLevelTableHeader'
  fieldContent?: RichTextContentType; // if present, precludes fetching of `field` from Contentful.
  functions?: React.MouseEventHandler<HTMLButtonElement>[];
  toolTips?: FormattedTooltipProps[];
  vars?: ReplacementVars | null;
  /* inlineParagraphs=true: Strips <p> tags, renders content inline. Use for single-line text (labels, tooltips).
  inlineParagraphs=false: Preserves <p> tags, renders paragraphs on separate lines. Use for multi-paragraph */
  inlineParagraphs: boolean;
  useStylingWrapper?: boolean;
  richTextStyling?: RichTextStyleWrapperProps;
  textReplacements?: TextReplacements;
  maxHeaderSize?: number; // header size that all rich text headers must be smaller than
}

const RichText = ({
  field,
  fieldContent = undefined,
  functions,
  toolTips = [],
  vars,
  inlineParagraphs,
  useStylingWrapper = false,
  textReplacements = undefined,
  richTextStyling,
  maxHeaderSize = 1,
}: RichTextProps) => {
  const { textMap, locale } = useTextContext();
  const { useContributionTerm } = usePremiumContext();

  let functionIndex = 0;
  let varIndex = 0;
  let tipIndex = 0;

  const incrementFunction = () => {
    if (functions && functions.length > 0) {
      try {
        const func = functions[functionIndex];
        functionIndex += 1;
        return func;
      } catch (error) {
        return undefined;
      }
    }
    return undefined;
  };

  const incrementVars = (vars) => {
    if (vars && vars.length > 0) {
      try {
        const variable = vars[varIndex];
        varIndex += 1;
        return variable;
      } catch (error) {
        return null;
      }
    }
    return undefined;
  };

  function replaceVars(node: React.ReactNode): React.ReactNode {
    if (!vars) {
      return undefined;
    }
    if (vars instanceof Array) {
      return incrementVars(vars);
    }
    return replaceVarsInNode(node, vars, field || null);
  }

  const incrementTips = (content) => {
    if (toolTips.length > 0) {
      try {
        const tip = toolTips[tipIndex];
        tipIndex += 1;
        return (
          <Tooltip
            id={`tooltip-${field}-${tipIndex}`}
            label={{
              text: tip.text,
              title: tip.title,
            }}
          >
            {/* TODO: Use the useUnderline prop in DL Tooltip instead of this underline styled components */}
            <TooltipUnderline>{tip.underline ? tip.underline : content}</TooltipUnderline>
          </Tooltip>
        );
      } catch (error) {
        return null;
      }
    }
    // Fail loudly: when the tooltip is missing return text as underlined without any tooltip so it is obvious we need to fix the tooltip
    return <u>{content}</u>;
  };

  const buildLink = (node, content) => (
    <a href={node.data.uri} target="_blank" rel="noreferrer">
      {content}
    </a>
  );

  // Possible rendering options
  let options: ContentfulOptions = {
    renderNode: {
      [INLINES.HYPERLINK]: (node, content) => buildLink(node, content),
    },
  };

  const functionOptions = {
    renderMark: {
      // eslint-disable-next-line react/no-unstable-nested-components
      [MARKS.BOLD]: (content) => (
        <button type="button" onClick={incrementFunction()}>
          {content}
        </button>
      ),
    },
    renderNode: {
      [INLINES.HYPERLINK]: (node, content) => buildLink(node, content),
    },
  };

  const varOptions = {
    renderMark: {
      [MARKS.CODE]: (node) => replaceVars(node),
      [MARKS.UNDERLINE]: (content) => incrementTips(content),
    },
    renderNode: {
      [INLINES.HYPERLINK]: (node, content) => buildLink(node, content),
    },
  };

  const noParagraphOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, content) => content,
      [INLINES.HYPERLINK]: (node, content) => buildLink(node, content),
    },
  };

  // If the customer uses contribution terms, replace "premium" with "contribution" in selected locale
  let allTextReplacements = {
    ...textReplacements,
  };

  if (useContributionTerm) {
    allTextReplacements = {
      ...allTextReplacements,
      ...PREMIUM_TEXT_REPLACEMENT[locale],
    };
  }

  // replace /n (which is inserted when contentful user types shift + enter in richText field) with <br/>
  const newlineOptions = {
    // taken from docs --> https://www.npmjs.com/package/@contentful/rich-text-react-renderer
    renderText: (text: string): JSX.Element[] => {
      return text.split('\n').map((textSegment, index) => (
        <React.Fragment key={index}>
          {index > 0 && <br />}
          {applyTextReplacements(textSegment, allTextReplacements)}
        </React.Fragment>
      ));
    },
  };

  // Find the largest (smallest number) heading in the content
  const findLargestHeading = (content: any): number | null => {
    if (!content) return null;
    let largestHeading: number | null = null;

    const traverse = (node: any) => {
      if (node.nodeType) {
        const headingMatch = node.nodeType.match(/^heading-(\d)$/);
        if (headingMatch) {
          const level = parseInt(headingMatch[1], 10);
          if (largestHeading === null || level < largestHeading) {
            largestHeading = level;
          }
        }
      }
      if (node.content && Array.isArray(node.content)) {
        node.content.forEach(traverse);
      }
    };

    traverse(content);
    return largestHeading;
  };

  // Adjust header levels based on maxHeaderSize
  // If maxHeaderSize is 2 and largest heading is h1, shift all headers down by 1 (h1->h2, h2->h3)
  // If largest heading is already <= maxHeaderSize, no changes needed
  const largestHeading = findLargestHeading(fieldContent || get(textMap, field as string));
  const shift = largestHeading && largestHeading < maxHeaderSize ? maxHeaderSize - largestHeading : 0;

  const headerOptions = {
    renderNode: {
      // eslint-disable-next-line react/no-unstable-nested-components
      [BLOCKS.HEADING_1]: (_, children) => React.createElement(`h${Math.min(1 + shift, 6)}`, null, children),
      // eslint-disable-next-line react/no-unstable-nested-components
      [BLOCKS.HEADING_2]: (_, children) => React.createElement(`h${Math.min(2 + shift, 6)}`, null, children),
      // eslint-disable-next-line react/no-unstable-nested-components
      [BLOCKS.HEADING_3]: (_, children) => React.createElement(`h${Math.min(3 + shift, 6)}`, null, children),
      // eslint-disable-next-line react/no-unstable-nested-components
      [BLOCKS.HEADING_4]: (_, children) => React.createElement(`h${Math.min(4 + shift, 6)}`, null, children),
      // eslint-disable-next-line react/no-unstable-nested-components
      [BLOCKS.HEADING_5]: (_, children) => React.createElement(`h${Math.min(5 + shift, 6)}`, null, children),
      // eslint-disable-next-line react/no-unstable-nested-components
      [BLOCKS.HEADING_6]: (_, children) => React.createElement(`h${Math.min(6 + shift, 6)}`, null, children),
    },
  };

  // Toggle options according to props
  let textField = fieldContent || get(textMap, field as string);

  if (vars || toolTips) {
    options = {
      ...options,
      renderNode: {
        ...options.renderNode,
        ...varOptions.renderNode,
      },
      renderMark: {
        ...options.renderMark,
        ...varOptions.renderMark,
      },
    };
  }
  if (functions) {
    options = {
      ...options,
      renderNode: {
        ...options.renderNode,
        ...functionOptions.renderNode,
      },
      renderMark: {
        ...options.renderMark,
        ...functionOptions.renderMark,
      },
    };
  }
  if (inlineParagraphs) {
    options = {
      ...options,
      renderNode: {
        ...options.renderNode,
        ...noParagraphOptions.renderNode,
      },
    };
  }

  options = {
    ...options,
    renderNode: {
      ...options.renderNode,
      ...headerOptions.renderNode,
    },
    ...newlineOptions,
  };

  // Custom list item renderer that unwraps paragraphs while preserving marks
  // Defined AFTER all options are merged so it can access the complete renderMark
  const listItemRenderer = (node, children) => {
    // The 'children' parameter contains the already-rendered content of the list item,
    // including paragraphs that have been wrapped in <p> tags by the default paragraph renderer.
    // We need to unwrap these <p> tags while preserving the inner content (including marks).

    // Process children to unwrap paragraph elements
    const processedChildren = React.Children.map(children, (child) => {
      // If this child is a <p> element, return its children instead
      if (React.isValidElement(child) && child.type === 'p') {
        return (child.props as { children?: React.ReactNode }).children;
      }
      // For other elements (like nested <ul>), keep them as-is
      return child;
    });

    return <li>{processedChildren}</li>;
  };

  // Add the list item renderer to options
  options = {
    ...options,
    renderNode: {
      ...options.renderNode,
      [BLOCKS.LIST_ITEM]: listItemRenderer,
    },
  };

  if (typeof textField === 'string') {
    Logging.error(
      `Text field "${field}" is not a Contentful Rich Text type. It is a string. Please check the Contentful field type.`,
    );
    return <ContentfulError field={field} />;
  }

  textField = documentToReactComponents(textField, options);

  if (textField === undefined) {
    textField = <ContentfulError field={field} />;
  }

  if (useStylingWrapper) {
    // Wrap in RichTextStyleWrapper to apply design system typography styles
    return <RichTextStyleWrapper {...richTextStyling}>{textField}</RichTextStyleWrapper>;
  }

  return textField;
};

const ContentfulError = ({ field }: { field: TextField | undefined }) => {
  return (
    <div id={field} className="contentful-error">
      {' '}
    </div>
  );
};

export { type ReplacementVars };
export default RichText;
